// Commission report DAO — read + RPC calls for commission_reports / commission_live.

import { BaseDao, type SupabaseClient } from '../base.ts';
import type { PostgrestError } from '@supabase/supabase-js';
import type { CommissionReport, CommissionPayee } from '@shared/types/commission.ts';

export interface LiveSummary {
  income: number;
  cost: number;
  transport: number;
  margin: number;
  commission: number;
  count: number;
  warnings: number;
}

type PayeeListRow = Pick<CommissionPayee, 'id' | 'name' | 'employee_id' | 'company_id'>;

export class CommissionReportDao extends BaseDao<CommissionReport> {
  protected readonly dependentResources = ['commission_reports', 'commission_live'];

  constructor(sb: SupabaseClient) {
    super(sb, 'commission_reports');
  }

  async getForMonth(payeeId: number, reportMonth: string): Promise<CommissionReport | null> {
    const { data, error } = await this.sb
      .from('commission_reports')
      .select('*')
      .eq('payee_id', payeeId)
      .eq('report_month', reportMonth)
      .eq('is_deleted', false)
      .limit(1);
    if (error) throw error;
    return (data?.[0] as CommissionReport | undefined) ?? null;
  }

  async computeLiveSummary(payeeId: number, reportMonth: string): Promise<LiveSummary | null> {
    const { data, error }: {
      data: Array<{
        total_income_pln: number | null;
        total_cost_pln: number | null;
        transport_pln: number | null;
        margin_pln: number | null;
        commission_pln: number | null;
        warnings: unknown[] | null;
      }> | null;
      error: PostgrestError | null;
    } = await this.sb
      .from('commission_live')
      .select('total_income_pln, total_cost_pln, transport_pln, margin_pln, commission_pln, warnings')
      .eq('payee_id', payeeId)
      .eq('report_month', reportMonth);
    if (error) throw error;
    if (!data || data.length === 0) return null;

    let income = 0, cost = 0, transport = 0, margin = 0, commission = 0, warnCount = 0;
    for (const r of data) {
      income    += Number(r.total_income_pln  ?? 0);
      cost      += Number(r.total_cost_pln    ?? 0);
      transport += Number(r.transport_pln     ?? 0);
      margin    += Number(r.margin_pln        ?? 0);
      commission += Number(r.commission_pln   ?? 0);
      warnCount += (r.warnings ?? []).length;
    }
    return {
      income:     Math.round(income     * 100) / 100,
      cost:       Math.round(cost       * 100) / 100,
      transport:  Math.round(transport  * 100) / 100,
      margin:     Math.round(margin     * 100) / 100,
      commission: Math.round(commission * 100) / 100,
      count:      data.length,
      warnings:   warnCount,
    };
  }

  async acceptReport(payeeId: number, reportMonth: string): Promise<unknown> {
    // The client is schema-untyped, so the RPC response is `any` — narrow it here.
    const { data, error } = (await this.sb.rpc('accept_commission_report', {
      p_payee_id: payeeId,
      p_report_month: reportMonth,
    })) as { data: unknown; error: PostgrestError | null };
    if (error) throw error;
    return data;
  }

  async revertReport(reportId: number): Promise<void> {
    const { error } = await this.sb.rpc('revert_commission_report', {
      p_report_id: reportId,
    });
    if (error) throw error;
  }

  /** Link (or unlink) a B2B invoice to a commission report. */
  async linkB2BInvoice(
    reportId: number,
    invoiceId: number | null,
    amountMatch: boolean | null,
  ): Promise<void> {
    const { error } = await this.sb
      .from('commission_reports')
      .update({ b2b_invoice_id: invoiceId, b2b_amount_match: amountMatch })
      .eq('id', reportId);
    if (error) throw error;
  }

  async listPayees(opts: { employeeId?: number; adminAll?: boolean }): Promise<PayeeListRow[]> {
    let q = this.sb
      .from('commission_payees')
      .select('id, name, employee_id, company_id')
      .eq('is_deleted', false)
      .order('name');
    if (opts.employeeId != null) q = q.eq('employee_id', opts.employeeId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as PayeeListRow[];
  }
}
